import { useMutation, useQueryClient } from '@tanstack/react-query';
import api, { DocumentSignEnvelopeResponse } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { addBreadcrumb, traceEnd, traceError, traceStart } from '@/lib/telemetry';

export function useCreateEnvelope() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      title: string;
      message?: string;
      signingOrder?: 'parallel' | 'sequential';
    }) => {
      const traceId = traceStart('document-sign:envelope-create');
      try {
        const result = await api.createDocumentSignEnvelope(input);
        traceEnd(traceId, { envelopeId: result.envelope.envelopeId });
        addBreadcrumb('document-sign', 'Envelope created');
        return result.envelope;
      } catch (err) {
        traceError(traceId, err instanceof Error ? err : String(err));
        throw err;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.documentSign.envelopes() });
    },
  });
}

export function useUpdateEnvelope(envelopeId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (patch: {
      title?: string;
      message?: string | null;
      signingOrder?: 'parallel' | 'sequential';
    }): Promise<DocumentSignEnvelopeResponse> => {
      const result = await api.updateDocumentSignEnvelope(envelopeId, patch);
      return result.envelope;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.documentSign.envelope(envelopeId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.documentSign.envelopes() });
    },
  });
}

export function useDeleteEnvelope() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (envelopeId: string) => {
      await api.deleteDocumentSignEnvelope(envelopeId);
      return envelopeId;
    },
    onSuccess: (envelopeId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.documentSign.envelope(envelopeId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.documentSign.envelopes() });
    },
  });
}

export function useSendEnvelope(envelopeId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const traceId = traceStart('document-sign:envelope-send', { envelopeId });
      try {
        const result = await api.sendDocumentSignEnvelope(envelopeId);
        traceEnd(traceId, { notified: result.notifiedRecipientIds.length });
        addBreadcrumb('document-sign', 'Envelope sent for signature', { envelopeId });
        return result;
      } catch (err) {
        traceError(traceId, err instanceof Error ? err : String(err));
        throw err;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.documentSign.envelope(envelopeId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.documentSign.envelopes() });
    },
  });
}

export function useVoidEnvelope(envelopeId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (reason: string) => api.voidDocumentSignEnvelope(envelopeId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.documentSign.envelope(envelopeId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.documentSign.envelopes() });
    },
  });
}
